import React, { useState, useEffect } from 'react';
import contactService from '../../services/contactService';
import './ContactMessagesManager.css';

function ContactMessagesManager() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // all, nouveau, lu, traité

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const response = await contactService.getMessages();
      setMessages(response.items || []);
    } catch (err) {
      setError('Erreur lors du chargement des messages');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (messageId, newStatus) => {
    try {
      if (newStatus === 'lu') {
        await contactService.markAsRead(messageId);
      } else if (newStatus === 'traité') {
        await contactService.markAsProcessed(messageId);
      }
      
      // Recharger les messages
      await loadMessages();
      
      // Mettre à jour le message sélectionné si c'est le même
      if (selectedMessage && selectedMessage.id === messageId) {
        const updatedMessage = messages.find(m => m.id === messageId);
        if (updatedMessage) {
          setSelectedMessage({ ...updatedMessage, status: newStatus });
        }
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
    }
  };

  const filteredMessages = messages.filter(message => {
    if (filter === 'all') return true;
    return message.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'nouveau': return '#e74c3c';
      case 'lu': return '#f39c12';
      case 'traité': return '#27ae60';
      default: return '#95a5a6';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="contact-messages-manager">
        <div className="loading">
          <div className="spinner"></div>
          <p>Chargement des messages...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contact-messages-manager">
        <div className="error">
          <h2>Erreur</h2>
          <p>{error}</p>
          <button onClick={loadMessages} className="retry-btn">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-messages-manager">
      <div className="manager-header">
        <h4>Messages</h4>
        <div className="stats">
          <div className="stat">
            <span className="stat-number">{messages.length}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat">
            <span className="stat-number">
              {messages.filter(m => m.status === 'nouveau').length}
            </span>
            <span className="stat-label">Nouveaux</span>
          </div>
          <div className="stat">
            <span className="stat-number">
              {messages.filter(m => m.status === 'traité').length}
            </span>
            <span className="stat-label">Traités</span>
          </div>
        </div>
      </div>

      <div className="filters">
        <button 
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          Tous ({messages.length})
        </button>
        <button 
          className={filter === 'nouveau' ? 'active' : ''}
          onClick={() => setFilter('nouveau')}
        >
          Nouveaux ({messages.filter(m => m.status === 'nouveau').length})
        </button>
        <button 
          className={filter === 'lu' ? 'active' : ''}
          onClick={() => setFilter('lu')}
        >
          Lus ({messages.filter(m => m.status === 'lu').length})
        </button>
        <button 
          className={filter === 'traité' ? 'active' : ''}
          onClick={() => setFilter('traité')}
        >
          Traités ({messages.filter(m => m.status === 'traité').length})
        </button>
        <button onClick={loadMessages} className="refresh-btn">
          🔄 Actualiser
        </button>
      </div>

      <div className="content">
        <div className="messages-list">
          {filteredMessages.length === 0 ? (
            <div className="no-messages">
              <p>Aucun message {filter !== 'all' ? `avec le statut "${filter}"` : ''}</p>
            </div>
          ) : (
            filteredMessages.map(message => (
              <div 
                key={message.id} 
                className={`message-item ${selectedMessage?.id === message.id ? 'selected' : ''}`}
                onClick={() => setSelectedMessage(message)}
              >
                <div className="message-header">
                  <div className="message-info">
                    <h3>{message.name}</h3>
                    <p className="message-email">{message.email}</p>
                  </div>
                  <div className="message-meta">
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(message.status) }}
                    >
                      {message.status}
                    </span>
                    <span className="message-date">
                      {formatDate(message.created)}
                    </span>
                  </div>
                </div>
                <div className="message-preview">
                  {message.message.substring(0, 100)}
                  {message.message.length > 100 && '...'}
                </div>
              </div>
            ))
          )}
        </div>

        <div className={`message-modal-overlay ${selectedMessage ? 'open' : ''}`} onClick={() => setSelectedMessage(null)}>
          <div className="message-modal-content" onClick={(e) => e.stopPropagation()}>
            {selectedMessage && (
              <>
                <div className="detail-header">
                  <div className="header-info">
                    <span className="status-badge" style={{ backgroundColor: getStatusColor(selectedMessage.status) }}>
                      {selectedMessage.status}
                    </span>
                    <h3>Message de {selectedMessage.name}</h3>
                  </div>
                  <button 
                    className="close-btn"
                    onClick={() => setSelectedMessage(null)}
                    title="Fermer"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="detail-content">
                  <div className="detail-grid">
                    <div className="detail-field">
                      <label>Nom</label>
                      <div className="field-value">
                        <span className="icon">👤</span> {selectedMessage.name}
                      </div>
                    </div>
                    
                    <div className="detail-field">
                      <label>Email</label>
                      <div className="field-value">
                        <span className="icon">📧</span>
                        <a href={`mailto:${selectedMessage.email}`}>
                          {selectedMessage.email}
                        </a>
                      </div>
                    </div>
                    
                    <div className="detail-field">
                      <label>Date de réception</label>
                      <div className="field-value">
                        <span className="icon">📅</span> {formatDate(selectedMessage.created)}
                      </div>
                    </div>
                    
                    <div className="detail-field">
                      <label>Changer le statut</label>
                      <select 
                        value={selectedMessage.status || 'nouveau'}
                        onChange={(e) => handleStatusChange(selectedMessage.id, e.target.value)}
                        className="status-select"
                      >
                        <option value="nouveau">Nouveau</option>
                        <option value="lu">Lu</option>
                        <option value="traité">Traité</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="detail-field full-width">
                    <label>Contenu du message</label>
                    <div className="message-text-box">
                      {selectedMessage.message}
                    </div>
                  </div>
                </div>
                
                <div className="detail-actions">
                  <a 
                    href={`mailto:${selectedMessage.email}?subject=Re: Votre message sur Couleur Sirène`}
                    className="reply-btn"
                  >
                    Répondre par email
                  </a>
                  
                  {selectedMessage.status === 'nouveau' && (
                    <button 
                      onClick={() => handleStatusChange(selectedMessage.id, 'lu')}
                      className="mark-read-btn"
                    >
                      Marquer comme lu
                    </button>
                  )}
                  
                  {selectedMessage.status !== 'traité' && (
                    <button 
                      onClick={() => handleStatusChange(selectedMessage.id, 'traité')}
                      className="mark-processed-btn"
                    >
                      Marquer comme traité
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactMessagesManager;
